import { Client } from '@sr-sdks/api-sdk-axios';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { queryOptionsGetClientsPaginated } from '../api/workspaces/clientApi';
import { MAX_CLIENT_IDS } from './useClientNames';

export type ClientNameOption = { label: string; value: string };

/** Stable identity, so the memos below don't recompute on every render. */
const EMPTY_CLIENTS: Client[] = [];

type UseClientNameSearchParams = {
  enabled?: boolean;
  /** Already debounced or URL-committed by the caller — this hook adds no delay. */
  searchTerm: string;
  workspaceId?: string;
};

/**
 * Matches a term against the workspace's client *names*, server-side.
 *
 * Replaces the old in-memory scan over a workspace-wide fetch, which the backend
 * truncated at 100 clients — so a client late in the alphabet could neither be
 * filtered on nor searched for (APP-2388).
 *
 * Feeds the Client filter's dropdown options and the blank-term baseline that
 * tells the Titles page whether the workspace has clients at all. The Titles
 * search no longer resolves its term here: it sends `clientName` and the titles
 * service resolves it to every matching client.
 */
export const useClientNameSearch = ({
  enabled = true,
  searchTerm,
  workspaceId,
}: UseClientNameSearchParams) => {
  const term = searchTerm.trim();

  const clientsQuery = useQuery({
    ...queryOptionsGetClientsPaginated({
      // `clientName`, not `searchTerm`: the broad param also matches
      // `clientReferenceId` and `shortId`, and the dropdown should offer the
      // same clients the Titles search matches by name. The Clients list page
      // picks `searchTerm` for the opposite reason.
      clientName: term || undefined,
      pageSize: MAX_CLIENT_IDS,
      workspaceId,
    }),
    enabled: enabled && Boolean(workspaceId),
  });

  const items = clientsQuery.data?.items ?? EMPTY_CLIENTS;
  /** The server count for this query, before the narrowing below. */
  const totalItems = clientsQuery.data?.meta?.totalItems ?? 0;

  /**
   * The backend matches `clientName` against the client name only
   * (`client.controller.ts`; it used to OR in the owning agent's
   * `workspaceName`, which matched every client of the workspace). The filter
   * below is a guard that keeps the hook's contract — real client-name matches
   * only — if the backend ever matches more broadly again, so no caller can
   * reintroduce the trap.
   */
  const clients = useMemo(() => {
    if (!term) return items;

    const lowered = term.toLowerCase();

    return items.filter((client) =>
      client.clientName?.toLowerCase().includes(lowered),
    );
  }, [items, term]);

  const clientOptions = useMemo<ClientNameOption[]>(
    () =>
      clients
        .filter((client) => client._id && client.clientName)
        .map((client) => ({ label: client.clientName, value: client._id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [clients],
  );

  return {
    clientOptions,
    clients,
    clientsQuery,
    /**
     * A failed search leaves no options, so the dropdown falls back to cmdk's
     * "No results found." — which reads as "this workspace has no such client"
     * rather than "the request failed". Holding the previous page instead does
     * not help: cmdk filters whatever it is given by the typed term, so a stale
     * list narrows to empty for exactly the terms that would need it. Surfaced
     * here so a caller can say something truer; editing the term re-queries.
     */
    isError: clientsQuery.isError,
    isPending: clientsQuery.isPending,
    isPlaceholderData: clientsQuery.isPlaceholderData,
    /** Total clients the workspace owns — only meaningful for a blank term. */
    totalClients: totalItems,
  };
};
